import React from "react";
import { createRoot } from "react-dom/client";
import { Router } from "react-router-dom";
import { createBrowserHistory } from "history";
import { FlagProvider, UnleashClient } from "@unleash/proxy-client-react";
import {
  init as initSentry,
  browserTracingIntegration,
  extraErrorDataIntegration,
  setTag as setSentryTag,
  addBreadcrumb as addSentryBreadcrumb,
  captureMessage as captureSentryMessage,
} from "@sentry/browser";
import "core-js/features/promise";

import App from "./App";
import ScrollToTop from "./components/atoms/ScrollToTop";
import { isUnSupportedBrowser, getUnleashConfig, getClientContextTags } from "./helpers/tools";
import { shouldFilterSentryError } from "./helpers/sentryFilter";
import { detectReloadLoop } from "./helpers/reloadLoopDetector";

import "./helpers/i18n";
import BrowserNotSupported from "./pages/BrowserNotSupported";

const SENTRY_DSN = import.meta.env.REACT_APP_SENTRY_DSN;
const isBrowserNotSupported = isUnSupportedBrowser();

if (SENTRY_DSN && !isBrowserNotSupported) {
  /* Tracing integration is included eagerly. We previously lazy-loaded it
   * via requestIdleCallback to defer ~250 KB raw of source from the main
   * entry chunk, but the actual built-bundle delta turned out to be ~5 KB
   * once Vite's tree-shaking ran, and the late init was leaving the
   * pageload transaction without span-level detail (FCP/LCP backfilled
   * fine via PerformanceObserver's buffered entries, but the spans
   * inside the transaction — Apollo queries, hydration, etc. — were
   * missing because no instrumentation was active during the load). The
   * 10% sampled tracing is more useful when complete than when slightly
   * cheaper to load. */
  initSentry({
    dsn: SENTRY_DSN,
    /* The deploy's commit SHA (empty locally). With events attributed to the
     * bundle that threw them, "old release" = a stale tab that predates the
     * current deploy vs. "current release" = a live serving/network problem. */
    release: __RELEASE__ || undefined,
    normalizeDepth: 6, // Default value: 3
    beforeSend(event) {
      const { exception, message } = event || {};
      const { values } = exception || {};
      const [errorValueItem] = values || [];
      const { mechanism, value, stacktrace } = errorValueItem || {};
      const { type } = mechanism || {};

      const errorMessage = value || message || "";
      if (shouldFilterSentryError(errorMessage, type, stacktrace?.frames)) {
        return null;
      }
      return event;
    },
    ignoreErrors: [/Blocked.*font/i, /Blocked.*img/i, /Blocked.*connect/i],
    integrations: [extraErrorDataIntegration(), browserTracingIntegration()],
    tracesSampleRate: 0.1,
  });

  /* Tag the client environment (in-app browser, iOS version, connectivity)
   * so the `TypeError: Load failed` cluster can be sliced by Instagram/
   * Facebook in-app browser vs. Safari and by device — a breakdown that
   * isn't searchable with the default tags. */
  Object.entries(getClientContextTags()).forEach(([key, value]) => setSentryTag(key, value));

  /* The WebKit process crash ("A problem repeatedly occurred") can't be caught
   * by the JS SDK, so detect its aftermath: too many checkout loads in a short
   * window => report it (and flush a Session Replay of the lead-up). */
  detectReloadLoop();
  /* Tag every Sentry event with the platform so web errors can be filtered
   * separately from any other sources. */
  setSentryTag("platform", "web");
}

// Adds support for Object.values to older browsers
if (!Object.values) Object.values = (obj: Record<string, unknown>) => Object.keys(obj).map((key) => obj[key]);

/* Vite fires vite:preloadError when a lazy chunk (or a CSS dependency of one)
 * fails to load — typically a content-hashed chunk from a previous deploy that
 * no longer exists. Recovery is AppErrorBoundary's job: left uncancelled, Vite
 * rethrows the original error, the import rejects through React.lazy into the
 * boundary, and its chunk-error handling reloads once. (Cancelling the event
 * here instead made Vite resolve the import with `undefined`, which React.lazy
 * then crashed on reading `.default` — the "undefined is not an object
 * (evaluating 'e._result.default')" cluster in Sentry.)
 *
 * This listener only records the failure. Chunk requests go through the
 * browser's module loader, not fetch/XHR, so Sentry gets no breadcrumb for
 * them, and the boundary deliberately keeps expected chunk errors out of
 * Sentry — this warning is the one searchable trace of which asset failed and
 * why. Sentry's transport uses keepalive fetch, so it survives the reload. */
window.addEventListener("vite:preloadError", (event) => {
  /* The prerenderer aborts stylesheet requests on purpose, so every
   * prerendered page fires this event by design — don't report those.
   * window.__PRERENDER__ is set by the function before any page script runs. */
  // eslint-disable-next-line no-underscore-dangle
  if (window.__PRERENDER__) {
    return;
  }
  const reason = String((event as Event & { payload?: Error }).payload?.message ?? "unknown");
  addSentryBreadcrumb({ category: "chunk", level: "error", message: reason });
  captureSentryMessage(`vite:preloadError: ${reason}`, "warning");
});

const history = createBrowserHistory();

const unleashClient = new UnleashClient(getUnleashConfig());

const container = document.getElementById("root")!;
const root = createRoot(container);

const renderApp = () => {
  root.render(
    isBrowserNotSupported ? (
      <BrowserNotSupported />
    ) : (
      <Router history={history}>
        <ScrollToTop>
          {/* Defer starting of client to until after loading user context in Layout */}
          <FlagProvider unleashClient={unleashClient} startClient={false}>
            <App history={history} />
          </FlagProvider>
        </ScrollToTop>
      </Router>
    )
  );
};

renderApp();
